import { CommonModule } from '@angular/common';
import { EmbeddedViewRef, input, TemplateRef, ViewContainerRef } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { takeWhileAlive } from '@core/frontend-shared';
import { combineLatest } from 'rxjs';
import { UiContentCenteredComponent } from '../content-centered';
import { UiSpinnerComponent } from '../spinner';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common";
function UiLoadingIndicatorComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "ui-content-centered");
    i0.ɵɵelement(1, "ui-spinner", 1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("min-height", ctx_r0.minHeight());
    i0.ɵɵadvance();
    i0.ɵɵproperty("label", ctx_r0.label());
  }
}
function UiLoadingIndicatorComponent_Conditional_1_Conditional_0_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function UiLoadingIndicatorComponent_Conditional_1_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, UiLoadingIndicatorComponent_Conditional_1_Conditional_0_ng_container_0_Template, 1, 0, "ng-container", 2);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.contentRefTemplate);
  }
}
function UiLoadingIndicatorComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵconditionalCreate(0, UiLoadingIndicatorComponent_Conditional_1_Conditional_0_Template, 1, 1, "ng-container");
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵconditional(ctx_r0.contentRefTemplate ? 0 : -1);
  }
}
/**
 * Some planned setups are impossible due to how angular works.
 * Some possibilities have been removed as they were sometimes causing UI not to update correctly.
 * In any case, the passed content will be rendered OUTSIDE of <ui-loading-indicator>!
 * this component can be used in many ways, so here are examples.
 *
 *
 * NOTE:
 * - content MUST be wrapped in a ng-template!
 * - the ng-template CANNOT use @if or any other structural directive.
 * - if you need to ng-let any data, the approach down below is recommended.
 *
 * <ui-loading-indicator [loading]="booleanState">
 * 	 <ng-template>any content<ng-template>
 * </ui-loading-indicator>
 *
 * or  ----------------------------------------------------------------------------------
 *
 * <ui-loading-indicator [loading]="booleanState" ></ui-loading-indicator>
 * @if(!booleanState){
 *     content...
 * }
 *
 *
 * TODO: animate fade in/fade out - due to the use of ng-template, use of angular animations is impossible.
 * must be done using manual css animation.
 */
export let UiLoadingIndicatorComponent = /*#__PURE__*/(() => {
  class UiLoadingIndicatorComponent {
    constructor(vcr) {
      this.vcr = vcr;
      this.loading = input(...(ngDevMode ? [undefined, {
        debugName: "loading"
      }] : /* istanbul ignore next */[]));
      this.label = input(null, ...(ngDevMode ? [{
        debugName: "label"
      }] : /* istanbul ignore next */[]));
      this.displayContentOutside = input(false, ...(ngDevMode ? [{
        debugName: "displayContentOutside"
      }] : /* istanbul ignore next */[]));
      this.minHeight = input('initial', ...(ngDevMode ? [{
        debugName: "minHeight"
      }] : /* istanbul ignore next */[]));
      combineLatest({
        loading: toObservable(this.loading)
      }).pipe(takeWhileAlive()).subscribe(({
        loading
      }) => {
        // remove view if already existing
        if (this.embeddedViewRef) {
          const index = this.vcr.indexOf(this.embeddedViewRef);
          if (index > -1) this.vcr.remove(index);
          this.embeddedViewRef = null;
        }
        // create content view if provided
        if (!loading) {
          if (this.contentRefTemplate && this.displayContentOutside()) {
            this.embeddedViewRef = this.vcr.createEmbeddedView(this.contentRefTemplate);
            this.embeddedViewRef.markForCheck();
          }
        }
      });
    }
    static {
      this.ɵfac = function UiLoadingIndicatorComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UiLoadingIndicatorComponent)(i0.ɵɵdirectiveInject(i0.ViewContainerRef));
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: UiLoadingIndicatorComponent,
        selectors: [["ui-loading-indicator"]],
        contentQueries: function UiLoadingIndicatorComponent_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, TemplateRef, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.contentRefTemplate = _t.first);
          }
        },
        hostVars: 4,
        hostBindings: function UiLoadingIndicatorComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("is-loading", ctx.loading())("hide-self", !ctx.loading() && ctx.displayContentOutside());
          }
        },
        inputs: {
          loading: [1, "loading"],
          label: [1, "label"],
          displayContentOutside: [1, "displayContentOutside"],
          minHeight: [1, "minHeight"]
        },
        decls: 2,
        vars: 2,
        consts: [[3, "minHeight"], [3, "label"], [4, "ngTemplateOutlet"]],
        template: function UiLoadingIndicatorComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵconditionalCreate(0, UiLoadingIndicatorComponent_Conditional_0_Template, 2, 3, "ui-content-centered", 0);
            i0.ɵɵconditionalCreate(1, UiLoadingIndicatorComponent_Conditional_1_Template, 1, 1);
          }
          if (rf & 2) {
            i0.ɵɵconditional(ctx.loading() ? 0 : -1);
            i0.ɵɵadvance();
            i0.ɵɵconditional(!ctx.loading() && !ctx.displayContentOutside() ? 1 : -1);
          }
        },
        dependencies: [CommonModule, i1.NgTemplateOutlet, UiSpinnerComponent, UiContentCenteredComponent],
        styles: [".is-loading[_nghost-%COMP%]{display:block}.hide-self[_nghost-%COMP%]{display:none}\n\n/*# sourceMappingURL=loading-indicator.ts-angular-inline--4.css.map*/"],
        changeDetection: 0
      });
    }
  }
  return UiLoadingIndicatorComponent;
})();